import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUserData, storeUserData } from "../../../services/apiService";
import {
  changeAccessCode,
  changePassword,
  fetchStaffCredentialStatus,
  parseCredentialStatus,
} from "../../../services/credentialPolicyService";
import {
  clearCredentialGate,
  mergeCredentialStatusIntoUser,
  PASSWORD_PATTERN,
  resolvePostLoginRoute,
  shouldSkipOldCredentialValidation,
  storeCredentialGateFromUser,
} from "../../../utils/credentialGateUtils";
import {
  CredentialAccessCodeRequirements,
  CredentialModalTabs,
  CredentialPasswordField,
  CredentialPasswordRequirements,
} from "./credentialChangeModalParts";
import "./ChangeCredentialsModal.scss";
import "./RequiredCredentialChange.scss";

const RequiredCredentialChange = ({ forced = true }) => {
  const navigate = useNavigate();
  const userData = getUserData();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const mustPassword = userData?.MustChangePassword || userData?.PasswordExpired;
  const mustAccessCode = userData?.MustChangeAccessCode || userData?.AccessCodeExpired;
  const skipOldValidation = shouldSkipOldCredentialValidation(userData);

  const [tab, setTab] = useState(() => {
    if (mustPassword) return "password";
    if (mustAccessCode) return "accessCode";
    return "password";
  });

  const [pwdForm, setPwdForm] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [codeForm, setCodeForm] = useState({
    newAccessCode: "",
    confirmAccessCode: "",
  });

  useEffect(() => {
    if (mustPassword) setTab("password");
    else if (mustAccessCode) setTab("accessCode");
  }, [mustPassword, mustAccessCode]);

  const title = useMemo(() => {
    if (userData?.PasswordExpired || userData?.AccessCodeExpired) {
      return "Your credentials have expired";
    }
    return "Change your login credentials";
  }, [userData]);

  const handleTabChange = (nextTab) => {
    if (nextTab === "password" && !mustPassword) return;
    if (nextTab === "accessCode" && !mustAccessCode) return;
    setTab(nextTab);
    setError("");
    setSuccess("");
  };

  const refreshStatusAndContinue = async () => {
    const response = await fetchStaffCredentialStatus(
      userData.StaffId,
      userData.OrganizationId,
    );
    const status = parseCredentialStatus(response);
    const merged = mergeCredentialStatusIntoUser(userData, status);
    storeUserData(merged);
    storeCredentialGateFromUser(merged);

    if (!status?.requiresCredentialChange) {
      clearCredentialGate();
      const hasMultipleOrgs =
        Array.isArray(merged.OtherOrgLink) && merged.OtherOrgLink.length > 1;
      navigate(resolvePostLoginRoute(merged, hasMultipleOrgs), { replace: true });
      return;
    }

    setSuccess("Saved. Please complete the remaining credential update.");
    if (status.mustChangeAccessCode) setTab("accessCode");
    else if (status.mustChangePassword) setTab("password");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const activeTab =
      tab === "password" && mustPassword
        ? "password"
        : tab === "accessCode" && mustAccessCode
          ? "accessCode"
          : mustPassword
            ? "password"
            : "accessCode";

    if (activeTab === "password") {
      if (!PASSWORD_PATTERN.test(pwdForm.newPassword)) {
        setError("Please meet all password requirements before continuing.");
        return;
      }
      if (pwdForm.newPassword !== pwdForm.confirmPassword) {
        setError("New password and confirm password do not match.");
        return;
      }

      setLoading(true);
      try {
        const response = await changePassword({
          StaffIdRef: userData.StaffId,
          OrganizationIdRef: userData.OrganizationId,
          OldPassword: "",
          NewPassword: pwdForm.newPassword,
          ConfirmPassword: pwdForm.confirmPassword,
          SkipOldValidation: skipOldValidation,
        });
        if (response.success !== 1) {
          setError(response.message || "Could not change password.");
          return;
        }
        setPwdForm({ newPassword: "", confirmPassword: "" });
        await refreshStatusAndContinue();
      } catch (err) {
        setError(err?.message || "Could not change password.");
      } finally {
        setLoading(false);
      }
      return;
    }

    const next = codeForm.newAccessCode.trim();
    const confirm = codeForm.confirmAccessCode.trim();
    if (next.length !== 6 || !/^\d{6}$/.test(next)) {
      setError("Please meet all access code requirements before continuing.");
      return;
    }
    if (next !== confirm) {
      setError("New access code and confirm access code do not match.");
      return;
    }

    setLoading(true);
    try {
      const response = await changeAccessCode({
        StaffIdRef: userData.StaffId,
        OrganizationIdRef: userData.OrganizationId,
        OldAccessCode: 0,
        NewAccessCode: parseInt(codeForm.newAccessCode, 10),
        ConfirmAccessCode: parseInt(codeForm.confirmAccessCode, 10),
        SkipOldValidation: skipOldValidation,
      });
      if (response.success !== 1) {
        setError(response.message || "Could not change access code.");
        return;
      }
      setCodeForm({ newAccessCode: "", confirmAccessCode: "" });
      await refreshStatusAndContinue();
    } catch (err) {
      setError(err?.message || "Could not change access code.");
    } finally {
      setLoading(false);
    }
  };

  const showPasswordForm =
    (tab === "password" && mustPassword) || (mustPassword && !mustAccessCode);
  const showAccessCodeForm =
    (tab === "accessCode" && mustAccessCode) || (!mustPassword && mustAccessCode);

  return (
    <div className="required-cred-page">
      <div className="required-cred-card credential-admin-modal">
        <div className="modal-content border-0 shadow">
          <div className="modal-header">
            <h5 className="modal-title mb-0">{title}</h5>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <p className="text-muted small mb-3">
                {forced
                  ? skipOldValidation
                    ? "Your credentials were reset, expired, or assigned by an administrator. Sign-in already verified your account — enter and confirm your new credentials below."
                    : "You must update the items below before you can access the system."
                  : "Update your password and/or access code."}
              </p>

              <CredentialModalTabs
                tab={tab}
                onTabChange={handleTabChange}
                showPassword={mustPassword}
                showAccessCode={mustAccessCode}
              />

              {error && (
                <div className="alert alert-danger py-2 px-3 small mb-3" role="alert">
                  {error}
                </div>
              )}
              {success && (
                <div className="alert alert-success py-2 px-3 small mb-3" role="alert">
                  {success}
                </div>
              )}

              {showPasswordForm && (
                <>
                  <CredentialPasswordRequirements password={pwdForm.newPassword} />
                  <CredentialPasswordField
                    id="required-new-password"
                    label="New password"
                    value={pwdForm.newPassword}
                    autoComplete="new-password"
                    onChange={(e) =>
                      setPwdForm((p) => ({ ...p, newPassword: e.target.value }))
                    }
                  />
                  <CredentialPasswordField
                    id="required-confirm-password"
                    label="Confirm new password"
                    value={pwdForm.confirmPassword}
                    autoComplete="new-password"
                    onChange={(e) =>
                      setPwdForm((p) => ({ ...p, confirmPassword: e.target.value }))
                    }
                  />
                </>
              )}

              {showAccessCodeForm && (
                <>
                  <CredentialAccessCodeRequirements
                    newAccessCode={codeForm.newAccessCode}
                    confirmAccessCode={codeForm.confirmAccessCode}
                  />
                  <CredentialPasswordField
                    id="required-new-access-code"
                    label="New access code"
                    value={codeForm.newAccessCode}
                    maxLength={6}
                    autoComplete="off"
                    onChange={(e) =>
                      setCodeForm((p) => ({
                        ...p,
                        newAccessCode: e.target.value.replace(/\D/g, "").slice(0, 6),
                      }))
                    }
                  />
                  <CredentialPasswordField
                    id="required-confirm-access-code"
                    label="Confirm new access code"
                    value={codeForm.confirmAccessCode}
                    maxLength={6}
                    autoComplete="off"
                    onChange={(e) =>
                      setCodeForm((p) => ({
                        ...p,
                        confirmAccessCode: e.target.value.replace(/\D/g, "").slice(0, 6),
                      }))
                    }
                  />
                </>
              )}
            </div>

            <div className="modal-footer">
              <button type="submit" className="btn btn-primary ms-auto" disabled={loading}>
                <i className="fas fa-check me-1" aria-hidden="true" />
                {loading
                  ? "Saving…"
                  : showPasswordForm
                    ? "Update password"
                    : "Update access code"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RequiredCredentialChange;
